// core/migrations.js — migração one-shot de chaves localStorage da v18.
//
// Origem: 13 chaves `mbo_*` mapeadas no docs/MIGRATION.md (seção "Inventário
// de localStorage"). Varredura feita a partir de legacy/App_Eloquence_v18_FREQ_COMPLETA.html.
//
// Estratégia (conservadora, zero perda):
//   1. Se a chave NOVA já existe → nada a fazer para essa chave.
//   2. Se a chave NOVA não existe mas a ANTIGA existe → copiar valor cru
//      para a nova (sem JSON.parse/stringify: copia a string literal, evita
//      corromper valores inesperados).
//   3. Chave antiga NÃO é removida aqui. Fica como fallback até v19.1.
//      Motivo: se o usuário voltar à v18 (rollback via legacy/ aberto no
//      navegador), dados dele continuam lá.
//   4. Chaves marcadas como `drop` (obsoletas) também NÃO são removidas —
//      apenas deixam de ser lidas pelo app. Limpeza explícita fica em
//      janela posterior com confirmação do usuário.
//
// IMPORTANTE: esta migração roda uma vez por boot, mas é idempotente.
// Pode ser chamada sempre sem efeito colateral.
//
// Pares `*_date`: na v18, chaves como `mbo_presenca` e `mbo_presenca_date`
// eram separadas — a `_date` armazenava `toLocaleString('pt-BR')` (string
// localizada, sem timezone) só para mostrar "última importação em..." na UI.
// NÃO são TTL. Preservamos as duas partes com renomeação simples; a
// consolidação eventual em envelope { v, exp } fica para quando cada módulo
// consumidor for refatorado (Fase 6).

/**
 * Mapa de migração. Ordem:
 *   - `from` (chave antiga literal no localStorage, SEM prefixo)
 *   - `to`   (chave lógica nova, SEM prefixo `eloq:` — ele é adicionado)
 *   - `drop` (boolean): true = chave abandonada, não copiar
 *
 * Nomes novos seguem convenção do MIGRATION.md.
 */
const MAP = [
  { from: 'mbo_alunos_cache',     to: 'alunos_cache' },
  { from: 'mbo_frequencia_v2',    to: 'frequencia' },
  { from: 'mbo_lembretes',        to: 'lembretes' },
  { from: 'mbo_conf_portal',      to: 'conf_portal' },
  { from: 'mbo_presenca',         to: 'presenca' },
  { from: 'mbo_presenca_date',    to: 'presenca_date' },
  { from: 'mbo_financeiro',       to: 'financeiro' },
  { from: 'mbo_financeiro_date',  to: 'financeiro_date' },
  { from: 'mbo_sheets_last_sync', to: 'sheets_last_sync' },
  { from: 'mbo_sheets_data',      to: 'sheets_data' },

  // Obsoletos — deploy deprecado (?action=all) e dados de programação
  // que viram API pura na Fase 4. Não copiamos.
  { from: 'mbo_dados_dinamicos',   drop: true },
  { from: 'mbo_programacao',       drop: true },
  { from: 'mbo_programacao_date',  drop: true },
];

const NEW_PREFIX = 'eloq:';
const FLAG_KEY = 'eloq:__migrations';

function safeGet(k) {
  try { return localStorage.getItem(k); } catch { return null; }
}

function safeSet(k, v) {
  try { localStorage.setItem(k, v); return true; } catch (e) {
    console.warn(`[migrations] setItem("${k}") falhou:`, e);
    return false;
  }
}

/**
 * Executa a migração. Idempotente: pode rodar todo boot, o gasto é desprezível.
 *
 * @returns {{ migrated: string[], skipped: string[], dropped: string[] }}
 *          Relatório útil para logs e para os testes.
 */
export function runMigrations() {
  const report = { migrated: [], skipped: [], dropped: [] };

  for (const entry of MAP) {
    const oldVal = safeGet(entry.from);

    if (entry.drop) {
      if (oldVal !== null) report.dropped.push(entry.from);
      continue;
    }

    const newKey = NEW_PREFIX + entry.to;
    const newExists = safeGet(newKey) !== null;

    if (newExists) {
      report.skipped.push(`${entry.from} → ${entry.to} (destino já existe)`);
      continue;
    }
    if (oldVal === null) {
      // Nada a migrar. Usuário novo ou aba sem dados prévios.
      continue;
    }

    if (safeSet(newKey, oldVal)) {
      report.migrated.push(`${entry.from} → ${entry.to}`);
    }
  }

  // Grava flag com timestamp para diagnóstico futuro (quantas vezes o boot
  // rodou, última migração bem-sucedida). Não bloqueia re-execução.
  const flag = { lastRun: Date.now(), version: 'v19.0.0-dev' };
  safeSet(FLAG_KEY, JSON.stringify(flag));

  return report;
}

/**
 * Exposto para testes: mapa literal.
 */
export const _MIGRATION_MAP = MAP;
